import { filterLocations, type PublicLocation } from '@irace/shared';
import { useEffect, useMemo, useRef, useState } from 'react';
import { COPY } from '../copy/vi.ts';

/**
 * M03 (§10) — location picker as a bottom sheet.
 *
 * Built on the native <dialog>: `showModal()` gives focus trapping, Escape to
 * close and inertness of the page behind it from the platform, which §21
 * requires and which a hand-rolled sheet gets wrong more often than not.
 */
export function LocationPicker({
  locations,
  selectedId,
  open,
  onSelect,
  onClose,
}: {
  locations: readonly PublicLocation[];
  selectedId: string | null;
  open: boolean;
  onSelect: (location: PublicLocation) => void;
  onClose: () => void;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (open && !dialog.open) {
      setQuery('');
      dialog.showModal();
      // Do not autofocus the search box: on iOS that opens the keyboard over
      // the list before the participant has seen it. Let them scroll first.
      dialog.focus();
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open]);

  const results = useMemo(() => filterLocations(locations, query), [locations, query]);

  return (
    <dialog
      ref={dialogRef}
      aria-label={COPY.picker.title}
      onClose={onClose}
      onCancel={onClose}
      // Fixed height rather than fit-to-content: as the participant types and
      // the result count drops, a shrinking sheet would slide the list out
      // from under their thumb between keystrokes.
      className="m-0 mt-auto h-[85dvh] w-full max-w-[480px] rounded-t-[20px] bg-surface p-0 backdrop:bg-[rgb(16_24_40_/_0.5)] sm:mx-auto"
    >
      <div className="flex h-full flex-col">
        <div className="flex items-center justify-between px-5 pt-4.5 pb-3">
          <h2 className="text-[17px] font-bold">{COPY.picker.title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={COPY.picker.close}
            className="flex size-9 items-center justify-center rounded-full bg-[#eef2f7] text-lg text-text-muted"
          >
            ×
          </button>
        </div>

        <div className="mx-5 mb-3 flex h-12 items-center gap-2 rounded-control border-[1.5px] border-border bg-bg px-3.5">
          <span aria-hidden="true">🔍</span>
          <input
            ref={searchRef}
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={COPY.picker.searchPlaceholder}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="none"
            spellCheck={false}
            enterKeyHint="search"
            className="h-full w-full bg-transparent text-base outline-none placeholder:text-[#98a4b5]"
          />
        </div>

        <p className="px-5 pb-2 text-xs text-[#98a4b5]">
          {COPY.picker.hintPrefix} · {locations.length} {COPY.picker.locationCountSuffix}
        </p>

        {/* §21 — result count is announced, not just visually apparent. */}
        <p className="sr-only" role="status" aria-live="polite">
          {results.length} {COPY.picker.locationCountSuffix}
        </p>

        <ul className="flex-1 overflow-y-auto overscroll-contain pb-[env(safe-area-inset-bottom)]">
          {results.length === 0 ? (
            <li className="px-5 py-8 text-center text-sm text-text-muted">{COPY.picker.empty}</li>
          ) : (
            results.map((location) => {
              const selected = location.id === selectedId;
              return (
                <li key={location.id}>
                  <button
                    type="button"
                    onClick={() => {
                      onSelect(location);
                    }}
                    aria-current={selected ? 'true' : undefined}
                    className={`flex min-h-11 w-full items-center justify-between gap-3 border-l-[3px] px-5 py-3.5 text-left text-base ${
                      selected
                        ? 'border-l-brand bg-[#ebf2ff] font-bold text-brand'
                        : 'border-l-transparent text-text'
                    }`}
                  >
                    <span>{location.name}</span>
                    <span
                      className={`rounded-md px-2 py-0.5 text-xs font-semibold ${
                        selected ? 'bg-brand text-brand-contrast' : 'bg-[#eef2f7] text-text-muted'
                      }`}
                    >
                      {location.code}
                    </span>
                  </button>
                </li>
              );
            })
          )}
        </ul>
      </div>
    </dialog>
  );
}
